import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from 'src/utils';
import { ErrorFallback } from 'src/utils/constants';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { Box, Flex, Text } from '@mantine/core';
import { stepAndRouteMap } from "src/constants";
import useCleverTapEvents from 'src/events/useGetEvents';
import { KYC_EVENT } from 'src/events/constant';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { RootState } from '../../redux/store';
import { useCommonStyles } from '../../styles/commonStyles';
import {
  clearDigioFormError, getDigioKycCredentials, setDigioFormStatus, verifyDigioKyc,
} from './digioFormSlice';
import { initDigio } from './initDigio';
import { useStyles } from './personalImageVerification.styles';
import { ScreenLoader } from '../../components/screenLoader';
import { getProgressStepper } from '../../utils/helpers';
import kycSheild from "../../assets/icons/kyc-shield.svg";
import kycFile from "../../assets/icons/kyc-file.svg";
import kyeEdit from "../../assets/icons/kyc-edit.svg";
import { Button } from '../../components/atoms';

export const KycVerification = (): JSX.Element => {
  const { classes } = useStyles();
  const { commonClasses } = useCommonStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const digioState = useSelector((state: RootState) => state.digioData);
  const idRef = useRef<any>();
  const { currentStep, nextStep } = useSelector((state:RootState) => state.account.userDetails);
  const { TriggerEvent } = useCleverTapEvents();

  useEffect(() => {
    dispatch(setDigioFormStatus('loading'));
    idRef.current = setTimeout(() => {
      dispatch(getDigioKycCredentials());
    }, 500);
    return () => {
      clearTimeout(idRef.current);
    };
  }, []);

  const digioCallback = useCallback((response: any) => {
    if (response?.error_code) {
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(response?.message) || ErrorFallback,
        icon: <IconError />,
      });
    } else {
      dispatch(verifyDigioKyc({ requestId: digioState?.kycCredentials?.requestId }));
    }
  }, [digioState?.kycCredentials?.requestId]);

  useEffect(() => {
    if (digioState.status === 'success') {
      if (digioState.isKycVerificationStarted && digioState.isKycVerified) {
        TriggerEvent(KYC_EVENT.kycReceived, {})
        notifications.show({
          color: 'green',
          message: 'KYC details verified successfully!',
          icon: <IconSuccess />,
        });
        dispatch(setDigioFormStatus('idle'));
        navigate(stepAndRouteMap[nextStep][0]);
      }
      dispatch(setDigioFormStatus('idle'));
    } else if (digioState.status === 'error') {
      dispatch(clearDigioFormError());
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(digioState.error.message) || ErrorFallback,
        icon: <IconError />,
      });
    }
  }, [digioState.status]);

  const onNextClick = () : void => {
    if (digioState.isKycVerificationStarted && digioState?.kycCredentials) {
      initDigio({
        callback: digioCallback,
        identifier: digioState?.kycCredentials?.identifier,
        requestId: digioState?.kycCredentials?.requestId,
        token: digioState?.kycCredentials?.token,
      });
      TriggerEvent(KYC_EVENT.kycVerification, {})
    } else {
      notifications.show({
        color: 'red',
        message: 'Something went wrong, Try again!',
        icon: <IconError />,
      });
    }
  };

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">
            KYC Verification
          </h2>
          <Text className="content_sm_regular text_fg_muted">
            Let’s get started with your verification
          </Text>
          <Text mt="1.5rem" className="content_xs_regular text_fg_subtle">
            Please have the following documents prepared to ensure a seamless account opening process:
          </Text>
          <Text mt="1.5rem" align="center" className="content_sm_semibold text_fg_default">
            Aadhaar Number, PAN Number, Pen & Paper
          </Text>
          <Flex mt="1.5rem" justify="center">
            <Box className={`${classes.aadharIcons} ${classes.zIndexTwo}`}>
              <img src={kycSheild} alt="kycSheild" />
            </Box>
            <Box className={`${classes.aadharIcons} ${classes.zIndexOne}`} ml="-1rem">
              <img src={kycFile} alt="kycFile" />
            </Box>
            <Box className={classes.aadharIcons} ml="-1rem">
              <img src={kyeEdit} alt="kyeEdit" />
            </Box>
          </Flex>
        </div>
        <div className="csFooter">
          <Button
            className={commonClasses.button}
            onClick={onNextClick}
          >
            Verify
          </Button>
        </div>
      </div>
      {digioState.status === 'loading' ? (<ScreenLoader />) : null}
    </ScreenWrapper>
  );
};

export default KycVerification;
